import React from "react";
import { X } from "lucide-react";

function getLowerPath(urlOrPath) {
  if (!urlOrPath) return "";
  try {
    return new URL(urlOrPath).pathname.toLowerCase();
  } catch {
    return String(urlOrPath).toLowerCase();
  }
}

function inferFileType({ url, fileName }) {
  const nameLower = (fileName || "").toLowerCase();
  const pathLower = getLowerPath(url);

  // If the display name does not contain an extension (e.g. it's just a label
  // like "Section 80D - Medical Insurance"), fall back to using the URL path
  // so we can still correctly infer the file type from the real object key.
  const lower = nameLower.includes(".") ? nameLower : pathLower;

  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".ppt") || lower.endsWith(".pptx")) return "ppt";
  if (/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(lower)) return "image";

  return "other";
}

export default function AttachmentPreviewModal({
  isOpen,
  url,
  downloadUrl,
  fileName,
  onClose,
}) {
  if (!isOpen) return null;

  const type = inferFileType({ url, fileName });

  const officeViewerUrl =
    type === "ppt" && url
      ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          url
        )}`
      : null;

  const resolvedDownloadUrl = downloadUrl || url;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">{fileName}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            type="button"
          >
            <X size={24} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-auto mb-4">
          {url ? (
            (() => {
              if (type === "pdf") {
                return (
                  <iframe
                    key={url}
                    src={url}
                    title={fileName}
                    className="w-full h-[500px] border-0"
                  />
                );
              }

              if (type === "ppt") {
                return (
                  <div className="w-full h-[500px]">
                    <iframe
                      key={officeViewerUrl}
                      src={officeViewerUrl}
                      title={fileName}
                      className="w-full h-[500px] border-0"
                    />
                  </div>
                );
              }

              if (type === "image") {
                return (
                  <img
                    src={url}
                    alt={fileName}
                    className="max-w-full max-h-[500px] mx-auto"
                  />
                );
              }

              return (
                <div className="flex flex-col items-center justify-center h-[500px] p-8">
                  <div className="text-gray-500 mb-4">
                    Preview not available for this file type
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                      Open
                    </a>
                    <a
                      href={resolvedDownloadUrl}
                      download={fileName}
                      className="px-4 py-2 bg-redhalo-mint/400 text-white rounded hover:opacity-95"
                    >
                      Download File
                    </a>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="flex items-center justify-center h-[500px]">
              <div className="text-gray-500">Loading...</div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 mr-2"
            >
              Open in new tab
            </a>
          )}
          {resolvedDownloadUrl && (
            <a
              href={resolvedDownloadUrl}
              download={fileName}
              className="px-4 py-2 bg-redhalo-mint/400 text-white rounded hover:opacity-95 mr-2"
            >
              Download
            </a>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
